import type { ReactNode } from "react";
import { XCircleIcon } from "@phosphor-icons/react/XCircle";
import { Button } from "../Controls/Button";
import { useControlPresentation } from "../Controls/Internal/useControlPresentation";
import {
  inputBaseStyle,
  underlineWrapper,
} from "../Controls/Internal/textInputStyles";

interface ConfirmedFieldProps {
  /** The confirmed value, shown bold. */
  value: ReactNode;
  /** Clears the value — wired to the × button. */
  onClear: () => void;
  /** Tooltip shown on the clear button. */
  clearTooltip?: string | undefined;
  /** `data-testid` for the clear button. */
  clearTestId?: string | undefined;
  /** `data-testid` for the value itself, which is text rather than an input. */
  valueTestId?: string | undefined;
}

/**
 * A read-only "confirmed value" field: a value the user has locked in (e.g. a
 * looked-up NMFC code), with a clear (×) button that fades in on hover —
 * present but quiet until you reach for it.
 *
 * Follows the form's ControlPresentation the way the text fields do. On an
 * `underlined` form it reads exactly like a filled field — the × is the only
 * thing marking it as locked in; boxing it regardless would make it the one
 * card in a form of bare underlines.
 */
export function ConfirmedField(props: ConfirmedFieldProps) {
  const presentation = useControlPresentation();

  const clearButton = (
    <span className="opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      <Button
        iconAction
        icon={XCircleIcon}
        iconWeight="fill"
        color="text-blue-text"
        tooltip={props.clearTooltip}
        onClick={props.onClear}
        data-testid={props.clearTestId}
      />
    </span>
  );

  if (presentation === "underlined") {
    return (
      <div
        className={`group flex items-center justify-between ${underlineWrapper(false)}`}
      >
        <span
          className={inputBaseStyle(false)}
          {...(props.valueTestId !== undefined && {
            "data-testid": props.valueTestId,
          })}
        >
          {props.value}
        </span>
        {clearButton}
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between rounded-lg border border-blue/30 border-b-border-strong bg-blue/8 px-3 py-2">
      <span
        className="text-xl font-bold text-content-secondary"
        {...(props.valueTestId !== undefined && {
          "data-testid": props.valueTestId,
        })}
      >
        {props.value}
      </span>
      {clearButton}
    </div>
  );
}
